import {
  collection,
  query,
  where,
  addDoc,
  serverTimestamp,
  onSnapshot,
  Timestamp,
  Unsubscribe,
  type DocumentData,
  type QuerySnapshot,
} from 'firebase/firestore';
import { auth, firestore } from '../firebase';
import { Message } from '../types/firestore';

// Define attachment type locally matching the structure in firestore.ts
// (Alternatively, import if defined in a shared types file)
interface ChatAttachment {
  type: 'image' | 'file';
  url: string;
  name: string;
}

/**
 * Wysyła nową wiadomość do Firestore.
 * @param repairRequestId - ID zlecenia, którego dotyczy wiadomość
 * @param senderId - UID nadawcy
 * @param receiverId - UID odbiorcy
 * @param content - Treść wiadomości (może być pusta, jeśli są załączniki)
 * @param attachments - Opcjonalna tablica załączników
 * @returns Promise<string> - ID nowo utworzonego dokumentu wiadomości
 */
const sendMessage = async (
  repairRequestId: string,
  senderId: string,
  receiverId: string,
  content: string, // Changed parameter name from text to content
  attachments?: ChatAttachment[] // Add optional attachments parameter
): Promise<string> => {
  // Allow sending attachments without text content
  if (
    !repairRequestId ||
    !senderId ||
    !receiverId ||
    (!content && (!attachments || attachments.length === 0))
  ) {
    throw new Error(
      'sendMessage: Brakuje wymaganych danych (repairRequestId, senderId, receiverId, content lub attachments).'
    );
  }

  const messagesCol = collection(firestore, 'messages');

  // Construct the message object based on the Message type from firestore.ts
  // Ensure all required fields from Message (excluding id/uid) are present
  const newMessageData: Omit<Message, 'id' | 'uid'> = {
    repairRequestId,
    requestId: repairRequestId, // Keep for potential compatibility, review later
    senderId,
    receiverId,
    content: content, // Use the content parameter
    attachments: attachments || [], // Include attachments, default to empty array
    createdAt: serverTimestamp() as Timestamp,
    isRead: false, // Default to unread
    // Remove obsolete/temp fields like text, imageUrl if 'content' and 'attachments' cover them
  };

  try {
    // Firestore automatically adds an 'id' field to the document reference
    const docRef = await addDoc(messagesCol, newMessageData);
    console.log(`Wysłano wiadomość o ID: ${docRef.id} dla zlecenia ${repairRequestId}`);
    return docRef.id;
  } catch (error) {
    console.error('Błąd podczas wysyłania wiadomości:', error);
    throw error; // Re-throw the error for handling in the component
  }
};

/**
 * Ustawia subskrypcję na wiadomości dla danego zlecenia naprawy.
 * @param repairRequestId - ID zlecenia naprawy
 * @param onMessagesUpdate - Funkcja callback wywoływana z tablicą wiadomości przy każdej aktualizacji
 * @param onError - Funkcja callback wywoływana w przypadku błędu
 * @returns Funkcja Unsubscribe do zakończenia nasłuchiwania
 */
const getMessagesSubscription = (
  repairRequestId: string,
  onMessagesUpdate: (messages: Message[]) => void,
  onError: (error: Error) => void
): Unsubscribe => {
  if (!repairRequestId) {
    console.warn('getMessagesSubscription: repairRequestId jest pusty.');
    // Return an empty unsubscribe function to avoid errors
    return () => {};
  }

  const currentUserId = auth.currentUser?.uid;
  if (!currentUserId) {
    console.warn('getMessagesSubscription: brak aktywnego użytkownika.');
    return () => {};
  }

  const messagesCol = collection(firestore, 'messages');
  const sentQuery = query(
    messagesCol,
    where('repairRequestId', '==', repairRequestId),
    where('senderId', '==', currentUserId)
  );
  const receivedQuery = query(
    messagesCol,
    where('repairRequestId', '==', repairRequestId),
    where('receiverId', '==', currentUserId)
  );

  const messageMap = new Map<string, Message>();

  const getMessageSortValue = (message: Message): number => {
    const createdAt = message.createdAt as any;
    if (createdAt && typeof createdAt.toMillis === 'function') {
      return createdAt.toMillis();
    }
    if (createdAt && typeof createdAt.toDate === 'function') {
      return createdAt.toDate().getTime();
    }
    return Number.MAX_SAFE_INTEGER;
  };

  const emitMessages = () => {
    const fetchedMessages = Array.from(messageMap.values());
    // Sort client-side to avoid brittle dependency on composite indexes.
    fetchedMessages.sort((a, b) => {
      const timestampDelta = getMessageSortValue(a) - getMessageSortValue(b);
      if (timestampDelta !== 0) {
        return timestampDelta;
      }
      return String(a.id || a.uid || '').localeCompare(String(b.id || b.uid || ''));
    });
    console.log(`Otrzymano ${fetchedMessages.length} wiadomości dla zlecenia ${repairRequestId}`);
    onMessagesUpdate(fetchedMessages);
  };

  const applySnapshot = (
    querySnapshot: QuerySnapshot<DocumentData>,
    ownedField: 'senderId' | 'receiverId'
  ) => {
    const seenInSnapshot = new Set<string>();
    querySnapshot.forEach((doc) => {
      seenInSnapshot.add(doc.id);
      messageMap.set(doc.id, { uid: doc.id, id: doc.id, ...doc.data() } as Message);
    });

    Array.from(messageMap.entries()).forEach(([id, message]) => {
      if (message[ownedField] === currentUserId && !seenInSnapshot.has(id)) {
        messageMap.delete(id);
      }
    });

    emitMessages();
  };

  const unsubscribeSent = onSnapshot(
    sentQuery,
    (querySnapshot) => {
      applySnapshot(querySnapshot, 'senderId');
    },
    (error) => {
      console.error(
        `Błąd subskrypcji wysłanych wiadomości dla zlecenia ${repairRequestId}:`,
        error
      );
      onError(error);
    }
  );

  const unsubscribeReceived = onSnapshot(
    receivedQuery,
    (querySnapshot) => {
      applySnapshot(querySnapshot, 'receiverId');
    },
    (error) => {
      console.error(
        `Błąd subskrypcji odebranych wiadomości dla zlecenia ${repairRequestId}:`,
        error
      );
      onError(error);
    }
  );

  return () => {
    unsubscribeSent();
    unsubscribeReceived();
  };
};

export const messageService = {
  sendMessage,
  getMessagesSubscription,
};
